import { sampleSkyAuthoredPose } from './115-sky-authored-pose.mjs';
import { skyLeviathanTriangleDistance } from './96-sky-leviathan-contacts.mjs';
import { skyGuardianThreat } from './88-sky-encounter-beat.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

const ANCHORS = ['anchor_left_joint', 'anchor_right_joint', 'anchor_rear_joint'];
const CROWNS = ['crown_upper_joint', 'crown_lower_joint'];
const CLIPS = ['Idle', 'AttackTell', 'Fire', 'RewindTell', 'Rewind', 'AnchorBreak', 'Defeat'];
export const SKY_HOURGLASS = Object.freeze({
  approach: 4,
  tell: 1.4,
  fire: 1,
  recovery: 3,
  rewindTell: 1.2,
  rewind: 2,
  anchorBreak: 2,
  snapshots: 40,
});
const END =
  SKY_HOURGLASS.tell + SKY_HOURGLASS.fire + SKY_HOURGLASS.recovery + SKY_HOURGLASS.rewindTell;
const regions = new WeakMap();
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const smooth = (n) => {
  const u = clamp(n, 0, 1);
  return u * u * (3 - 2 * u);
};
const copyTransform = (transform) => ({
  position: [...transform.position],
  quaternion: [...transform.quaternion],
  scale: [...transform.scale],
});
function multiply(a, b) {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}
function compileRegions(rig) {
  if (regions.has(rig)) return regions.get(rig);
  const meshRegions = rig.meshes.map((mesh) => {
    if (!mesh.jointNodes) return null;
    const owner = (index) => {
      let node = -1;
      for (let slot = 0; slot < 4; slot++) {
        const weight = mesh.weights[index * 4 + slot];
        if (weight > 0.000001) {
          if (Math.abs(weight - 1) > 0.000001 || node !== -1) return -1;
          node = mesh.jointNodes[mesh.joints[index * 4 + slot]];
        }
      }
      return node;
    };
    const result = [];
    for (let i = 0; i < mesh.indices.length; i += 3) {
      const nodes = mesh.indices.slice(i, i + 3).map(owner);
      if (nodes[0] < 0 || nodes.some((node) => node !== nodes[0])) return null;
      result.push(rig.nodes[nodes[0]].name);
    }
    return result;
  });
  if (
    meshRegions.some((value) => !value) ||
    ![...ANCHORS, ...CROWNS, 'hourglass_core_joint'].every((name) =>
      meshRegions.some((values) => values.includes(name)),
    )
  )
    return null;
  regions.set(rig, meshRegions);
  return meshRegions;
}
function roleAt(boss, contact) {
  const mask = compileRegions(boss.authoredRig),
    meshIndex = contact.meshIndex ?? 0,
    mesh = boss.authoredPose.meshes[meshIndex];
  if (!mask || !mesh) return null;
  const v = mesh.vertices,
    ids = mesh.indices;
  for (let i = 0; i < ids.length; i += 3) {
    const a = ids[i] * 3,
      b = ids[i + 1] * 3,
      c = ids[i + 2] * 3;
    const ab = [v[b] - v[a], v[b + 1] - v[a + 1], v[b + 2] - v[a + 2]],
      ac = [v[c] - v[a], v[c + 1] - v[a + 1], v[c + 2] - v[a + 2]];
    const n = [
        ab[1] * ac[2] - ab[2] * ac[1],
        ab[2] * ac[0] - ab[0] * ac[2],
        ab[0] * ac[1] - ab[1] * ac[0],
      ],
      length = Math.hypot(...n);
    if (
      length < 1e-12 ||
      Math.abs(
        (contact.x - v[a]) * n[0] + (contact.y - v[a + 1]) * n[1] + (contact.z - v[a + 2]) * n[2],
      ) >
        length * 0.0001
    )
      continue;
    const point = (index) => ({ x: v[index], y: v[index + 1], z: v[index + 2] });
    if (skyLeviathanTriangleDistance(contact, { a: point(a), b: point(b), c: point(c) }) < 0.0001)
      return mask[meshIndex][i / 3];
  }
  return null;
}

/** Bind only the inspected rigid-weight source with all six mechanisms. Anchor
 * health is independent of the rewindable armor; no clock or prop grants damage. */
export function createSkyHourglassKing(state, boss) {
  const rig = boss?.authoredRig;
  if (
    boss?.worldId !== 'hourglass-king' ||
    !rig ||
    !compileRegions(rig) ||
    !CLIPS.every((name) => rig.clips.some((clip) => clip.name === name)) ||
    !['hourglass_core', 'crown_emitter', ...ANCHORS.map((_, i) => `time_anchor_${i}`)].every(
      (name) => rig.sockets.some((socket) => socket.name === name),
    )
  )
    return false;
  const z = -36 + rig.bounds.min[2],
    y = 2 - rig.bounds.min[1] + skyTerrainHeight(0, z, state.terrainTravel ?? 0);
  const broken = sampleSkyAuthoredPose(rig, 'AnchorBreak', 2),
    left = rig.nodes.findIndex((node) => node.name === ANCHORS[0]);
  const delta = broken.nodes[left].position.map((v, i) => v - rig.nodes[left].position[i]);
  boss.hourglass = {
    phase: 'approach',
    age: 0,
    clock: 0,
    loop: 0,
    base: { x: 0, y, z },
    anchors: ANCHORS.map((name, index) => ({
      index,
      node: rig.nodes.findIndex((node) => node.name === name),
      hp: boss.maxHp / 3,
      maxHp: boss.maxHp / 3,
      breakAge: null,
    })),
    breakDelta: delta,
    overrides: [],
    rootTransform: null,
    debt: 0,
    armorDamage: 0,
    restoreFrom: boss.maxHp,
    shots: 0,
    history: [],
    echoes: [],
    snapshotAge: 0,
    impacts: [],
    released: false,
    pattern: [
      { x: -8, y: clamp(state.y + 4, 6, 25) },
      { x: 0, y: clamp(state.y + 9, 11, 30) },
      { x: 8, y: clamp(state.y + 14, 16, 35) },
    ],
  };
  boss.hourglass.rootTransform = path(boss.hourglass, 0, -48);
  return true;
}
function path(h, clock, approach = 0) {
  const first = SKY_HOURGLASS.tell,
    last = first + SKY_HOURGLASS.fire;
  let x, y, z, yaw;
  if (clock < first) {
    const u = smooth(clock / first);
    x = -4 + 8 * u;
    y = 1.5 * u;
    z = 5 * u;
    yaw = 0.8 * u;
  } else if (clock < last) {
    x = 4;
    y = 1.5;
    z = 5;
    yaw = 0.8;
  } else {
    const u = smooth((clock - last) / SKY_HOURGLASS.recovery);
    x = 4 - 8 * u;
    y = 1.5 * (1 - u);
    z = 5 * (1 - u);
    yaw = 0.8 + (Math.PI - 0.8) * u;
  }
  const angle = Math.PI + yaw;
  return {
    position: [h.base.x + x, h.base.y + y, h.base.z + z + approach],
    quaternion: [0, Math.sin(angle / 2), 0, Math.cos(angle / 2)],
    scale: [1, 1, 1],
  };
}
function timeline(clock) {
  if (clock < SKY_HOURGLASS.tell) return { phase: 'tell', clip: 'AttackTell', seconds: clock };
  if (clock < SKY_HOURGLASS.tell + SKY_HOURGLASS.fire)
    return { phase: 'fire', clip: 'Fire', seconds: clock - SKY_HOURGLASS.tell };
  if (clock < END - SKY_HOURGLASS.rewindTell)
    return {
      phase: 'recovery',
      clip: 'Idle',
      seconds: clock - SKY_HOURGLASS.tell - SKY_HOURGLASS.fire,
    };
  return {
    phase: 'rewind-tell',
    clip: 'RewindTell',
    seconds: clock - END + SKY_HOURGLASS.rewindTell,
  };
}
function anchorOverrides(boss, dt) {
  const h = boss.hourglass;
  h.overrides = h.anchors.map((anchor) => {
    const rest = boss.authoredRig.nodes[anchor.node];
    if (anchor.breakAge !== null) anchor.breakAge += dt;
    const u = anchor.breakAge === null ? 0 : smooth(anchor.breakAge / SKY_HOURGLASS.anchorBreak),
      direction = anchor.index === 0 ? -1 : 1;
    return {
      node: anchor.node,
      position: rest.position.map((v, i) => v + h.breakDelta[i] * u),
      quaternion: multiply(rest.quaternion, [
        0,
        Math.sin((direction * 0.13 * u) / 2),
        0,
        Math.cos((0.13 * u) / 2),
      ]),
      scale: [...rest.scale],
    };
  });
}

/** Replay a fixed authored attack timeline and reverse its actual spatial path.
 * Armor restoration is visible HP recovery; queued damage and broken anchors
 * persist. The returned transform replaces the generic guardian drift in116. */
export function stepSkyHourglassKing(state, dt, { event }) {
  const boss = state.boss,
    h = boss?.hourglass;
  if (!h) return null;
  h.age += dt;
  anchorOverrides(boss, dt);
  let beat;
  if (h.phase === 'approach') {
    h.rootTransform = path(h, 0, -48 * (1 - smooth(h.age / SKY_HOURGLASS.approach)));
    beat = { clip: 'Idle', seconds: h.age };
    if (h.age >= SKY_HOURGLASS.approach) {
      h.phase = 'tell';
      h.age = 0;
      h.clock = 0;
      boss.authoredTransition = boss.authoredPose;
    }
  } else if (h.phase === 'anchor-break') {
    const destination = path(h, END),
      u = smooth(h.age / SKY_HOURGLASS.anchorBreak),
      start = h.breakRoot ?? h.rootTransform,
      sign =
        start.quaternion.reduce((sum, v, i) => sum + v * destination.quaternion[i], 0) < 0 ? -1 : 1,
      q = start.quaternion.map((v, i) => v + (destination.quaternion[i] * sign - v) * u),
      norm = Math.hypot(...q);
    h.rootTransform = {
      position: start.position.map((v, i) => v + (destination.position[i] - v) * u),
      quaternion: q.map((v) => v / norm),
      scale: [1, 1, 1],
    };
    beat = { clip: 'AnchorBreak', seconds: h.age };
    if (h.age >= SKY_HOURGLASS.anchorBreak) {
      h.phase = 'rewind-tell';
      h.age = 0;
      h.clock = END - SKY_HOURGLASS.rewindTell;
      boss.authoredTransition = boss.authoredPose;
    }
  } else if (h.phase === 'rewind') {
    const u = clamp(h.age / SKY_HOURGLASS.rewind, 0, 1);
    h.clock = END * (1 - u);
    h.rootTransform = path(h, h.clock);
    boss.hp = h.restoreFrom + (boss.maxHp - h.restoreFrom) * smooth(u);
    h.armorDamage = (boss.maxHp - boss.hp) / boss.maxHp;
    beat = { clip: 'Rewind', seconds: h.age };
    if (h.age >= SKY_HOURGLASS.rewind) {
      h.loop++;
      h.phase = 'tell';
      h.age = 0;
      h.clock = 0;
      h.shots = 0;
      h.history = [];
      h.echoes = [];
      boss.hp = boss.maxHp;
      boss.authoredTransition = boss.authoredPose;
      event(state, 'hourglass-loop', 'THE SAME ATTACK RETURNS · ANCHOR DAMAGE PERSISTS');
    }
  } else {
    h.clock += dt;
    const next = timeline(Math.min(END, h.clock));
    if (h.phase !== next.phase) {
      h.phase = next.phase;
      h.age = next.seconds;
      boss.authoredTransition = boss.authoredPose;
      event(
        state,
        'hourglass-phase',
        next.phase === 'rewind-tell'
          ? 'CROWNS REVERSING · REWIND INCOMING'
          : 'LEARN THE THREE SHOTS · BREAK THE TIME ANCHORS',
      );
    }
    h.rootTransform = path(h, h.clock);
    beat = next;
    if (h.clock >= END) {
      h.phase = 'rewind';
      h.age = 0;
      h.restoreFrom = boss.hp;
      boss.authoredTransition = boss.authoredPose;
      event(state, 'hourglass-rewind', 'ARMOR REWIND · ANCHORS KEEP THEIR DAMAGE');
    }
  }
  const alive = h.anchors.find((anchor) => anchor.hp > 0);
  return {
    ...beat,
    exposed: h.phase !== 'approach',
    target: alive ? `time_anchor_${alive.index}` : 'hourglass_core',
  };
}
function emitPattern(state, index) {
  const boss = state.boss,
    h = boss.hourglass,
    origin = boss.authoredPose.sockets.crown_emitter,
    target = h.pattern[index];
  const flight = Math.max(0.7, (SKY_WORLD.pilotZ - origin.z) / 24);
  state.threats.push({
    id: state.nextId++,
    kind: 'threat',
    ...origin,
    vx: (target.x - origin.x) / flight,
    vy: (target.y - origin.y) / flight,
    ...skyGuardianThreat(boss, origin, flight),
    hourglassLoop: h.loop,
    hourglassShot: index,
  });
}
function snapshot(boss) {
  return {
    clock: boss.hourglass.clock,
    rootTransform: copyTransform(boss.rootTransform),
    nodes: boss.authoredPose.nodes.map((node) => ({
      position: [...node.position],
      quaternion: [...node.quaternion],
      scale: [...node.scale],
    })),
  };
}

/** Project current source anchor sockets and finite attack/echo state after116
 * samples the pose. Repeated cycles use identical stored aim lanes, never a
 * hidden retarget. Echoes contain source transforms only and are non-solid. */
export function finishSkyHourglassKing(state, dt) {
  const boss = state.boss,
    h = boss?.hourglass;
  if (!h || boss.defeated) return;
  if (h.phase === 'fire') {
    const age = h.clock - SKY_HOURGLASS.tell,
      due = Math.min(3, Math.floor(age / 0.33) + 1);
    while (h.shots < due) {
      emitPattern(state, h.shots);
      h.shots++;
    }
  }
  h.snapshotAge += dt;
  if (!['rewind', 'approach', 'anchor-break'].includes(h.phase) && h.snapshotAge >= 0.125) {
    h.snapshotAge = 0;
    h.history.push(snapshot(boss));
    if (h.history.length > SKY_HOURGLASS.snapshots) h.history.shift();
  }
  const reference = h.phase === 'rewind' ? h.clock : Infinity;
  const candidates = h.history.filter((frame) => frame.clock <= reference);
  h.echoes = [3, 6, 9]
    .map((distance, index) => {
      const frame = candidates[candidates.length - distance];
      return frame ? { ...frame, opacity: 0.17 - index * 0.035 } : null;
    })
    .filter(Boolean);
  h.impacts = h.impacts.filter((impact) => state.time - impact.time < 4);
  boss.weakpoints = h.anchors
    .filter((anchor) => anchor.hp > 0)
    .map((anchor) => ({
      ...boss.authoredPose.sockets[`time_anchor_${anchor.index}`],
      id: `time_anchor_${anchor.index}`,
      label: `TIME ANCHOR ${anchor.index + 1}`,
      radius: 2,
      criticalRadius: 1,
      exposed: h.phase !== 'approach',
    }));
  boss.weakpoint = boss.weakpoints[0] ?? {
    ...boss.authoredPose.sockets.hourglass_core,
    id: 'hourglass_core',
    radius: 2,
    criticalRadius: 1,
    exposed: true,
  };
  boss.anatomy.targets = boss.weakpoints;
  boss.anatomy.primary = boss.weakpoint;
  boss.authoredFlightCeiling = Math.max(
    boss.authoredFlightCeiling ?? 9,
    ...boss.weakpoints.map((point) => point.y + 5),
  );
  const broken = h.anchors.filter((anchor) => anchor.hp <= 0).length;
  boss.controlHint = `${broken}/3 ANCHORS BROKEN · ${Math.round(h.debt)} DAMAGE DELAYED · ${h.phase === 'rewind' ? 'ANCHOR DAMAGE SURVIVES REWIND' : 'AIM AT EXTERIOR CLOCKS'}`;
}

/** Classify only a real nearest source triangle. Anchor hits persist, while
 * ordinary armor damage is delayed and its visible HP rewinds. Final anchor
 * failure releases accumulated manual damage once; autonomous hits cannot farm it. */
export function resolveSkyHourglassContact(state, target, shot, contact, effect, { event }) {
  const boss = state.boss,
    h = boss?.hourglass;
  if (!h || target !== boss) return null;
  if (effect.autonomous || h.released || h.phase === 'approach')
    return { handled: true, damage: 0, quality: 'armor' };
  const role = roleAt(boss, contact);
  if (!role) return { handled: true, damage: 0, quality: 'armor' };
  const index = ANCHORS.indexOf(role),
    anchor = h.anchors[index];
  let damage;
  if (anchor) {
    damage = Math.min(anchor.hp, effect.damage * 1.35);
    anchor.hp = Math.max(0, anchor.hp - damage);
    h.debt = Math.min(boss.maxHp * 4, h.debt + damage);
    if (anchor.hp < 1e-7) {
      anchor.hp = 0;
      if (anchor.breakAge === null) {
        anchor.breakAge = 0;
        h.breakRoot = copyTransform(h.rootTransform);
        h.phase = 'anchor-break';
        h.age = 0;
        boss.authoredTransition = boss.authoredPose;
        event(
          state,
          'hourglass-anchor',
          `TIME ANCHOR ${index + 1} BROKEN · REWIND CANNOT RESTORE IT`,
        );
      }
    }
    if (h.anchors.every((value) => value.hp === 0) && !h.released) {
      h.released = true;
      boss.hp = Math.max(0, boss.maxHp - h.debt);
      if (boss.hp < 1e-6) boss.hp = 0;
      event(state, 'hourglass-collapse', 'TIME CONTROL FAILED · ALL DELAYED DAMAGE ARRIVES');
    }
  } else {
    damage = Math.min(Math.max(0, boss.hp - 1), effect.damage * 0.6);
    boss.hp -= damage;
    h.debt = Math.min(boss.maxHp * 4, h.debt + damage);
    h.armorDamage = (boss.maxHp - boss.hp) / boss.maxHp;
  }
  if (damage > 0) {
    h.impacts.push({
      x: contact.x,
      y: contact.y,
      z: contact.z,
      time: state.time,
      anchor: index >= 0,
    });
    if (h.impacts.length > 24) h.impacts.shift();
  }
  return { handled: true, damage, quality: anchor ? 'critical' : 'body' };
}
